/* ==========================================================================
   Seasonal Halloween layer — behaviour (bats, optional sound, self-expiry)
   See docs/HALLOWEEN_THEME.md. Pumpkins and accents are pure CSS
   (assets/bloxshop-halloween.css); this file only adds what CSS cannot.

   Loaded by layout/theme.liquid ONLY while the layer is on. The server hands
   over the seconds left until the end date (data-ttl on the script tag); no
   client clock, locale, or location is read to decide anything, so the stop
   time cannot drift with a visitor's device settings.

     - bats: once per browser session, never with reduced motion, created after
       `load` so they cannot compete with the first paint
     - sound: synthesised with Web Audio (no files, nothing to download), created
       only inside a user gesture, quiet, and always behind a mute button
     - expiry: when the deadline passes in a tab that is still open (or restored
       from the back/forward cache) everything is removed without a reload
   ========================================================================== */
(function () {
  'use strict';

  var script = document.getElementById('bs-halloween-js');
  var root = document.documentElement;
  if (!script || !root.classList.contains('bs-halloween')) return;

  var ttl = parseInt(script.getAttribute('data-ttl'), 10);
  if (!(ttl > 0)) return;

  var deadline = Date.now() + ttl * 1000;
  var cfg = {
    intensity: script.getAttribute('data-intensity') || 'normal',
    bats: script.getAttribute('data-bats') === 'true',
    sounds: script.getAttribute('data-sounds') === 'true',
    mutedByDefault: script.getAttribute('data-muted-default') !== 'false',
    logo: script.getAttribute('data-logo') || ''
  };

  var KEY_BATS = 'bs-hw-bats';      // sessionStorage: bats already flew this session
  var KEY_CUE = 'bs-hw-cue';        // sessionStorage: first-gesture chime already played
  var KEY_SOUND = 'bs-hw-sound';    // localStorage: the visitor's own on/off choice

  var host = document.getElementById('bs-halloween');
  var batLayer = host && host.querySelector('[data-hw-bats]');
  var toggle = document.querySelector('[data-hw-sound]');
  var reduced = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  var expiryTimer = 0;
  var batTimer = 0;
  var batsFlying = false;
  var finished = false;

  /* Storage can throw (private mode, blocked site data): every access is guarded
     and the page behaves identically without it. */
  function read(store, key) {
    try { return window[store].getItem(key); } catch (e) { return null; }
  }
  function write(store, key, value) {
    try { window[store].setItem(key, value); return true; } catch (e) { return false; }
  }

  /* ── Expiry ─────────────────────────────────────────────────────────── */

  function expired() { return Date.now() >= deadline; }

  function teardown() {
    if (finished) return;
    finished = true;
    window.clearTimeout(expiryTimer);
    window.clearTimeout(batTimer);
    audio.stop();
    restoreLogos();
    if (host && host.parentNode) host.parentNode.removeChild(host);
    if (toggle && toggle.parentNode) toggle.parentNode.removeChild(toggle);
    root.className = root.className.replace(/(^|\s)bs-(halloween|hw-[a-z]+)(?=\s|$)/g, ' ').replace(/\s+/g, ' ').trim();
    var link = document.querySelector('link[href*="bloxshop-halloween.css"]');
    if (link && link.parentNode) link.parentNode.removeChild(link);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pageshow', check);
  }

  /* ── Logo ───────────────────────────────────────────────────────────────
     CSS already swaps the picture where `content: url()` works on <img>; this
     covers the rest. The originals are kept so teardown can put them back. */

  var LOGO_SEL = 'img.bs-logo__img, img.bs-flogo__img';

  function swapLogos() {
    if (!cfg.logo || finished) return;
    var imgs = document.querySelectorAll(LOGO_SEL);
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.hasAttribute('data-hw-logo')) continue;
      img.setAttribute('data-hw-logo', '1');
      img.setAttribute('data-hw-src', img.getAttribute('src') || '');
      if (img.hasAttribute('srcset')) { img.setAttribute('data-hw-srcset', img.getAttribute('srcset')); img.removeAttribute('srcset'); }
      img.setAttribute('src', cfg.logo);
    }
  }

  function restoreLogos() {
    var imgs = document.querySelectorAll(LOGO_SEL + ', img[data-hw-logo]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (!img.hasAttribute('data-hw-logo')) continue;
      img.setAttribute('src', img.getAttribute('data-hw-src') || '');
      if (img.hasAttribute('data-hw-srcset')) img.setAttribute('srcset', img.getAttribute('data-hw-srcset'));
      img.removeAttribute('data-hw-logo');
      img.removeAttribute('data-hw-src');
      img.removeAttribute('data-hw-srcset');
    }
  }

  function check() {
    if (finished) return;
    if (expired()) { teardown(); return; }
    schedule();
  }

  function schedule() {
    window.clearTimeout(expiryTimer);
    /* setTimeout overflows past ~24.8 days; re-arm in chunks until the deadline. */
    expiryTimer = window.setTimeout(check, Math.min(deadline - Date.now(), 2147000000));
  }

  /* ── Sound ──────────────────────────────────────────────────────────── */

  var audio = (function () {
    var AC = window.AudioContext || window.webkitAudioContext;
    var ctx = null;
    var master = null;

    function ensure() {
      if (!AC) return null;
      if (!ctx) {
        try {
          ctx = new AC();
          master = ctx.createGain();
          master.gain.value = 0.7;
          master.connect(ctx.destination);
        } catch (e) { ctx = null; return null; }
      }
      return ctx;
    }

    /* Call from inside a user gesture only. */
    function unlock() {
      var c = ensure();
      if (c && c.state === 'suspended' && c.resume) c.resume();
      return c;
    }

    function running() { return !!ctx && ctx.state === 'running'; }

    /* A soft two-note descending bell: a confirmation, not an alarm. */
    function chime() {
      if (!running()) return;
      var t = ctx.currentTime;
      [[587.33, 0], [466.16, 0.16]].forEach(function (n) {
        var osc = ctx.createOscillator();
        var g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(n[0], t + n[1]);
        g.gain.setValueAtTime(0.0001, t + n[1]);
        g.gain.exponentialRampToValueAtTime(0.07, t + n[1] + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + n[1] + 0.9);
        osc.connect(g);
        g.connect(master);
        osc.start(t + n[1]);
        osc.stop(t + n[1] + 1);
      });
    }

    /* Filtered noise with a fast tremolo: leathery wing flutter, very quiet. */
    function whoosh(seconds) {
      if (!running()) return;
      var t = ctx.currentTime;
      var len = Math.max(0.6, seconds || 1.6);
      var frames = Math.floor(ctx.sampleRate * len);
      var buf = ctx.createBuffer(1, frames, ctx.sampleRate);
      var data = buf.getChannelData(0);
      for (var i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
      var src = ctx.createBufferSource();
      src.buffer = buf;
      var band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.Q.value = 1.4;
      band.frequency.setValueAtTime(380, t);
      band.frequency.linearRampToValueAtTime(900, t + len * 0.5);
      band.frequency.linearRampToValueAtTime(420, t + len);
      var env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, t);
      env.gain.linearRampToValueAtTime(0.05, t + len * 0.35);
      env.gain.linearRampToValueAtTime(0.0001, t + len);
      var lfo = ctx.createOscillator();
      var lfoDepth = ctx.createGain();
      lfo.frequency.value = 15;
      lfoDepth.gain.value = 0.02;
      lfo.connect(lfoDepth);
      lfoDepth.connect(env.gain);
      src.connect(band);
      band.connect(env);
      env.connect(master);
      src.start(t);
      lfo.start(t);
      src.stop(t + len);
      lfo.stop(t + len);
    }

    function suspend() { if (ctx && ctx.state === 'running' && ctx.suspend) ctx.suspend(); }
    function resume() { if (ctx && ctx.state === 'suspended' && ctx.resume) ctx.resume(); }
    function stop() { if (ctx && ctx.close) { try { ctx.close(); } catch (e) { /* already closed */ } } ctx = null; }

    return { supported: !!AC, unlock: unlock, running: running, chime: chime, whoosh: whoosh, suspend: suspend, resume: resume, stop: stop };
  })();

  var soundOn = false;

  function setSound(on, persist) {
    soundOn = on;
    if (toggle) toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (persist) write('localStorage', KEY_SOUND, on ? 'on' : 'off');
    if (!on) audio.suspend();
  }

  /* Home for the button: the sticky header, next to the other icon buttons, so it
     is always reachable and never floats over product copy. If the header is not
     there (or changes shape) it simply stays in its fixed bottom corner. */
  function place() {
    if (!toggle) return;
    var anchor = document.querySelector('.bs-nav-right .bs-icon-btn');
    if (anchor && anchor.parentNode && toggle.parentNode !== anchor.parentNode) {
      anchor.parentNode.insertBefore(toggle, anchor);
      toggle.classList.add('bs-hw-sound--header');
    }
  }

  function initSound() {
    if (!cfg.sounds || !audio.supported || !toggle) {
      /* No audio support or sound switched off: the control must not exist. */
      if (toggle && toggle.parentNode) toggle.parentNode.removeChild(toggle);
      toggle = null;
      return;
    }

    var saved = read('localStorage', KEY_SOUND);
    setSound(saved ? saved === 'on' : !cfg.mutedByDefault, false);
    place();
    toggle.hidden = false;
    /* The theme editor re-renders the header group; put the button back. */
    document.addEventListener('shopify:section:load', place);

    toggle.addEventListener('click', function () {
      var turningOn = !soundOn;
      setSound(turningOn, true);
      if (turningOn) {
        audio.unlock();
        audio.resume();
        /* Resuming is asynchronous in some browsers; cue on the next turn. */
        window.setTimeout(function () {
          audio.chime();
          playBats(true);
        }, 60);
      }
    });

    /* Visitor already opted in (earlier visit, or the default is "unmuted"):
       browsers still require a gesture before any sound, so wait for the first
       one, play a single soft chime per session, then stay quiet. */
    if (soundOn && !read('sessionStorage', KEY_CUE)) {
      var arm = function () {
        ['pointerdown', 'keydown', 'touchend'].forEach(function (ev) { document.removeEventListener(ev, arm, true); });
        if (finished || !soundOn) return;
        write('sessionStorage', KEY_CUE, '1');
        audio.unlock();
        window.setTimeout(audio.chime, 60);
      };
      ['pointerdown', 'keydown', 'touchend'].forEach(function (ev) { document.addEventListener(ev, arm, true); });
    }
  }

  /* ── Bats ───────────────────────────────────────────────────────────── */

  var BAT_SVG =
    '<svg viewBox="0 0 64 36" aria-hidden="true" focusable="false">' +
      '<g class="bs-hw-bat__wing bs-hw-bat__wing--l"><path d="M30 17C24 6 12 3 1 7c5 3 6 7 6 11 3-2 6-1 8 2 2-3 5-4 8-2 3-1 5-1 7 0z" fill="#1B0B2E"/></g>' +
      '<g class="bs-hw-bat__wing bs-hw-bat__wing--r"><path d="M34 17c6-11 18-14 29-10-5 3-6 7-6 11-3-2-6-1-8 2-2-3-5-4-8-2-3-1-5-1-7 0z" fill="#1B0B2E"/></g>' +
      '<ellipse cx="32" cy="19" rx="5.5" ry="8" fill="#1B0B2E"/>' +
      '<path d="M27.5 13l1.5-8 3 5 3-5 1.5 8z" fill="#1B0B2E"/>' +
      '<circle cx="30" cy="15.5" r="1.2" fill="#FF9F1C"/><circle cx="34" cy="15.5" r="1.2" fill="#FF9F1C"/>' +
    '</svg>';

  function batCount() {
    var n = cfg.intensity === 'subtle' ? 3 : cfg.intensity === 'festive' ? 10 : 6;
    return window.innerWidth < 640 ? Math.ceil(n * 0.6) : n;
  }

  function rand(min, max) { return min + Math.random() * (max - min); }

  /* With session storage blocked there is no "already flew" flag, so fall back to
     the referrer: arriving from another page of this store is not a fresh visit. */
  function cameFromThisStore() {
    try { return !!document.referrer && new URL(document.referrer).host === window.location.host; } catch (e) { return false; }
  }

  function playBats(force) {
    if (finished || !cfg.bats || !batLayer || batsFlying || reduced.matches) return;
    if (!force) {
      if (read('sessionStorage', KEY_BATS)) return;
      if (!write('sessionStorage', KEY_BATS, '1') && cameFromThisStore()) return;
    }

    var small = window.innerWidth < 640;
    var count = batCount();
    var longest = 0;
    batsFlying = true;
    batLayer.innerHTML = '';

    for (var i = 0; i < count; i++) {
      var dur = rand(6.5, 10);
      var delay = i * 0.55 + rand(0, 0.7);
      var el = document.createElement('div');
      el.className = 'bs-hw-bat' + (Math.random() < 0.4 ? ' bs-hw-bat--rev' : '');
      el.style.setProperty('--hw-y', rand(8, 68).toFixed(1) + 'vh');
      el.style.setProperty('--hw-size', Math.round(small ? rand(26, 40) : rand(34, 58)) + 'px');
      el.style.setProperty('--hw-dur', dur.toFixed(2) + 's');
      el.style.setProperty('--hw-delay', delay.toFixed(2) + 's');
      el.style.setProperty('--hw-bob', rand(1.2, 2).toFixed(2) + 's');
      el.innerHTML = '<span class="bs-hw-bat__bob">' + BAT_SVG + '</span>';
      batLayer.appendChild(el);
      longest = Math.max(longest, dur + delay);
    }

    if (audio.running() && soundOn) audio.whoosh(Math.min(longest, 3));

    window.clearTimeout(batTimer);
    batTimer = window.setTimeout(function () {
      if (batLayer) batLayer.innerHTML = '';
      batsFlying = false;
    }, longest * 1000 + 400);
  }

  function startBats() {
    if (document.hidden) {
      /* Don't spend the one fly-by on a background tab. */
      var wait = function () {
        if (document.hidden) return;
        document.removeEventListener('visibilitychange', wait);
        playBats(false);
      };
      document.addEventListener('visibilitychange', wait);
      return;
    }
    playBats(false);
  }

  /* ── Lifecycle ──────────────────────────────────────────────────────── */

  function onVisibility() {
    if (document.hidden) { audio.suspend(); return; }
    check();
    if (soundOn) audio.resume();
  }

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pageshow', check);   /* back/forward-cache restore after the end date */

  /* Reduced motion switched on mid-visit: stop any bats in the air. */
  if (reduced.addEventListener) {
    reduced.addEventListener('change', function () {
      if (reduced.matches && batLayer) { batLayer.innerHTML = ''; batsFlying = false; }
    });
  }

  schedule();
  swapLogos();
  document.addEventListener('shopify:section:load', swapLogos);
  initSound();

  if (cfg.bats && !reduced.matches) {
    var begin = function () { batTimer = window.setTimeout(startBats, 500); };
    if (document.readyState === 'complete') begin();
    else window.addEventListener('load', begin, { once: true });
  }
})();
